
'use client';

import { useState } from 'react';
import {
  differenceInYears,
  differenceInMonths,
  differenceInDays,
  differenceInWeeks,
  format,
  parse,
  isValid,
  addYears,
  addMonths,
} from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Cake,
  PartyPopper,
  Gift,
  TrendingUp,
} from 'lucide-react';
import { getDayOfWeek, getNextBirthday } from '@/lib/date-utils';

type AgeDetails = {
  years: number;
  months: number;
  days: number;
  dayOfBirth: string;
  totalDays: number;
  totalWeeks: number;
  nextBirthdayDate: Date;
  daysUntilNext: number;
  nextBirthdayDay: string;
};

export function AgeCalculator() {
  const [dobString, setDobString] = useState<string>('');
  const [ageDetails, setAgeDetails] = useState<AgeDetails | null>(null);
  const [error, setError] = useState<string | null>(null);


  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, ''); // Remove all non-digits
    const valueLength = rawValue.length;
    let formattedValue = rawValue;

    if (valueLength > 2) {
      formattedValue = `${rawValue.slice(0, 2)}/${rawValue.slice(2)}`;
    }
    if (valueLength > 4) {
      formattedValue = `${rawValue.slice(0, 2)}/${rawValue.slice(2, 4)}/${rawValue.slice(4, 8)}`;
    }

    setDobString(formattedValue);

    if (formattedValue.length < 10) {
      setAgeDetails(null);
      setError(null);
      if (formattedValue.length > 0) {
        setError('Please enter a full date.');
      }
      return;
    }

    const parsedDate = parse(formattedValue, 'dd/MM/yyyy', new Date());

    if (isValid(parsedDate)) {
      setError(null);
      const now = new Date();
      const years = differenceInYears(now, parsedDate);
      const dateAfterYears = addYears(parsedDate, years);
      const months = differenceInMonths(now, dateAfterYears);
      const days = differenceInDays(now, addMonths(dateAfterYears, months));
      
      const nextBirthdayDate = getNextBirthday(format(parsedDate, 'yyyy-MM-dd'));
      
      setAgeDetails({
        years,
        months,
        days,
        dayOfBirth: getDayOfWeek(parsedDate),
        totalDays: differenceInDays(now, parsedDate),
        totalWeeks: differenceInWeeks(now, parsedDate),
        nextBirthdayDate: nextBirthdayDate,
        daysUntilNext: Math.ceil(differenceInDays(nextBirthdayDate, now)),
        nextBirthdayDay: getDayOfWeek(nextBirthdayDate),
      });

    } else {
      setError('Invalid date. Please use DD/MM/YYYY format.');
      setAgeDetails(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Age Calculator</CardTitle>
        <CardDescription>
          Instantly calculate age and get other fun date insights.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-2 max-w-xs">
          <label className="text-sm font-medium" htmlFor="dob-input">Date of Birth</label>
          <Input
            id="dob-input"
            type="text"
            placeholder="DD/MM/YYYY"
            value={dobString}
            onChange={handleDateChange}
            className="w-full"
            maxLength={10}
          />
           {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        {ageDetails && (
          <div className="space-y-4">
            <div>
              <h3 className="mb-2 text-lg font-semibold">Calculated Age 🎂</h3>
              <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4 rounded-md border p-4">
                <div className="flex-1 text-center">
                  <p className="text-4xl font-bold">{ageDetails.years}</p>
                  <p className="text-sm text-muted-foreground">Years</p>
                </div>
                <div className="flex-1 text-center">
                  <p className="text-4xl font-bold">{ageDetails.months}</p>
                  <p className="text-sm text-muted-foreground">Months</p>
                </div>
                <div className="flex-1 text-center">
                  <p className="text-4xl font-bold">{ageDetails.days}</p>
                  <p className="text-sm text-muted-foreground">Days</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="mb-2 text-lg font-semibold">Additional Insights 📅</h3>
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      Born On
                    </CardTitle>
                    <Cake className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {ageDetails.dayOfBirth}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      Next Birthday
                    </CardTitle>
                    <PartyPopper className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {ageDetails.nextBirthdayDay}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {format(ageDetails.nextBirthdayDate, 'MMMM do')}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      Countdown
                    </CardTitle>
                    <Gift className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {ageDetails.daysUntilNext} days
                    </div>
                  </CardContent>
                </Card>
                 <Card>
                  <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      Total Lived
                    </CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-xl font-bold">
                      {ageDetails.totalDays.toLocaleString()} days
                    </div>
                     <p className="text-xs text-muted-foreground">
                      or {ageDetails.totalWeeks.toLocaleString()} weeks
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
